import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent
} from "react";
import { cx } from "shared/utils";
import { Show } from "shared/components";
import { AvailabilityOverviewTeamMember } from "api/scheduling/types";
import {
  AVAILABLE_FILL_CLASS,
  HOUR_COL_PX,
  MEMBER_NAME_COL_PX,
  MEMBER_ROW_PX,
  UNAVAILABLE_REASON_COPY
} from "./constants";
import {
  buildDayRegions,
  findDay,
  formatHourLabel,
  getShowingDisplay,
  formatTime,
  minutesToPx,
  showingGeometry
} from "./helpers";
import { UnavailableRegion } from "./UnavailableRegion";

interface DayTimelineProps {
  teamMembers: AvailabilityOverviewTeamMember[];
  selectedDate: string;
  timezone: string;
  bandStartHour: number;
  bandEndHour: number;
}

// Height of the sticky time-axis header row that carries the hour labels.
const HEADER_PX = 48;

// Upper bound on the scrollable grid height, per the Figma spec (the calendar
// scrolls internally rather than growing the whole page — QA prefers a
// Slack-like body scroll over a full-page scroll). On shorter viewports the
// grid shrinks below this so the whole card still fits on screen and only the
// grid — not the page — scrolls (avoids the double scroll).
const GRID_MAX_HEIGHT_PX = 600;

// Never collapse the grid below this, so it stays usable on very short screens.
const GRID_MIN_HEIGHT_PX = 260;

// Breathing room kept below the grid inside the scroll container, on top of
// the container's own bottom padding (subtracted separately).
const GRID_BOTTOM_GAP_PX = 12;

// A showing card narrower than this cannot fit horizontal text, so the time
// range is dropped and the label turns vertical.
const CARD_COMPACT_PX = 40;

// Pointer travel before a press turns into a pan rather than a click.
const PAN_THRESHOLD_PX = 4;

interface PanOrigin {
  x: number;
  y: number;
  scrollLeft: number;
  scrollTop: number;
}

// Nearest ancestor that scrolls vertically — the grid is capped to this
// element's visible height so it never spills past the fold and forces a second
// (page-level) scrollbar on top of its own internal one.
const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el = node?.parentElement ?? null;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if (
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay"
    )
      return el;
    el = el.parentElement;
  }
  return null;
};

const reasonCopy = (
  reason: keyof typeof UNAVAILABLE_REASON_COPY | null,
  memberName: string
): string | null => {
  if (!reason) return null;
  const copy = UNAVAILABLE_REASON_COPY[reason];
  if (!copy) return null;
  return copy.replace("{name}", memberName);
};

export const DayTimeline = ({
  teamMembers,
  selectedDate,
  timezone,
  bandStartHour,
  bandEndHour
}: DayTimelineProps) => {
  const hourLabels = Array.from(
    { length: bandEndHour - bandStartHour + 1 },
    (_, i) => bandStartHour + i
  );
  const bandHours = bandEndHour - bandStartHour;

  // Cap the grid to the visible height of its scroll container so the card
  // always fits on screen and only the grid scrolls internally — a fixed height
  // taller than the viewport forced a second, page-level scrollbar. The same
  // pass measures the width available to the time track.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(GRID_MAX_HEIGHT_PX);
  const [hourPx, setHourPx] = useState(HOUR_COL_PX);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const recompute = () => {
      // Stretch hours to fill the card; wider bands keep their size and scroll.
      const trackWidth = el.clientWidth - MEMBER_NAME_COL_PX;
      setHourPx(
        bandHours > 0
          ? Math.max(HOUR_COL_PX, trackWidth / bandHours)
          : HOUR_COL_PX
      );

      const parent = getScrollParent(el);
      if (!parent) {
        setMaxHeight(GRID_MAX_HEIGHT_PX);
        return;
      }
      const offsetInParent =
        el.getBoundingClientRect().top - parent.getBoundingClientRect().top;
      // clientHeight spans the content box + padding; the grid card cannot sit
      // inside the container's bottom padding, so exclude it too.
      const paddingBottom =
        parseFloat(getComputedStyle(parent).paddingBottom) || 0;
      const available =
        parent.clientHeight -
        offsetInParent -
        paddingBottom -
        GRID_BOTTOM_GAP_PX;
      setMaxHeight(
        Math.max(
          GRID_MIN_HEIGHT_PX,
          Math.min(GRID_MAX_HEIGHT_PX, Math.round(available))
        )
      );
    };

    recompute();
    // The sidebar toggle resizes the card without firing a window resize.
    const observer = new ResizeObserver(recompute);
    observer.observe(el);
    window.addEventListener("resize", recompute);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [bandHours, teamMembers.length]);

  const timelineWidth = bandHours * hourPx;

  // Engages only past PAN_THRESHOLD_PX so clicks and hover tooltips still work.
  const panOrigin = useRef<PanOrigin | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  const startPan = (event: PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || event.button !== 0) return;

    panOrigin.current = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop
    };
  };

  const movePan = (event: PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    const origin = panOrigin.current;
    if (!el || !origin) return;

    const dx = event.clientX - origin.x;
    const dy = event.clientY - origin.y;
    if (!isPanning) {
      if (Math.hypot(dx, dy) < PAN_THRESHOLD_PX) return;
      setIsPanning(true);
      el.setPointerCapture(event.pointerId);
    }

    el.scrollLeft = origin.scrollLeft - dx;
    el.scrollTop = origin.scrollTop - dy;
  };

  const endPan = (event: PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (el?.hasPointerCapture(event.pointerId))
      el.releasePointerCapture(event.pointerId);

    panOrigin.current = null;
    setIsPanning(false);
  };

  const columns = useMemo(
    () =>
      teamMembers.map((member) => {
        const day = findDay(member, selectedDate);
        return {
          member,
          regions: buildDayRegions(day, timezone, bandStartHour, bandEndHour),
          showings: day?.showings ?? []
        };
      }),
    [teamMembers, selectedDate, timezone, bandStartHour, bandEndHour]
  );

  return (
    // The grid body scrolls INTERNALLY inside a bounded box (QA prefers a
    // Slack-like body scroll over a full-page scroll). `scroll-on-hover` keeps
    // the scrollbar hidden until the container is hovered, so it does not
    // clutter the view when idle. `isolate` keeps the internal z-indexes in this
    // component's stacking context so the sticky header does not paint over the
    // expanded sidebar (aside is z-[14]). The grid scrolls on BOTH axes: the
    // time-axis header sticks to the top, the member-name column to the left.
    // w-0 + min-w-full: the definite width stops the wide track propagating up
    // as a min-content floor, which grew the page and let the shell scroll
    // instead of this box — breaking both sticky axes.
    <div
      ref={scrollRef}
      className={cx(
        "scroll-on-hover isolate w-0 min-w-full overflow-auto",
        isPanning ? "cursor-grabbing select-none" : "cursor-grab"
      )}
      style={{ maxHeight }}
      onPointerDown={startPan}
      onPointerMove={movePan}
      onPointerUp={endPan}
      onPointerCancel={endPan}
    >
      {/* Header row — time-axis labels, sticky to the top while the grid
          scrolls vertically through the member lanes */}
      <div className="sticky top-0 z-30 flex w-max min-w-full">
        {/* Top-left corner — sticky on both axes */}
        <div
          className="sticky left-0 z-10 shrink-0 border-b border-r border-alpha-80 bg-white"
          style={{ width: MEMBER_NAME_COL_PX, height: HEADER_PX }}
        />
        <div
          className="relative shrink-0 border-b border-alpha-80 bg-white"
          style={{ width: timelineWidth, height: HEADER_PX }}
        >
          {hourLabels.map((hour) => (
            <div
              key={hour}
              className={cx(
                "absolute bottom-0 flex flex-col items-center gap-1",
                hour === bandStartHour && "translate-x-0",
                hour === bandEndHour && "-translate-x-full",
                hour !== bandStartHour &&
                  hour !== bandEndHour &&
                  "-translate-x-1/2"
              )}
              style={{ left: minutesToPx((hour - bandStartHour) * 60, hourPx) }}
            >
              <span className="whitespace-nowrap text-sm font-medium text-content-200">
                {formatHourLabel(hour)}
              </span>
              <span aria-hidden="true" className="h-2 w-px bg-alpha-80" />
            </div>
          ))}
        </div>
      </div>

      {columns.map(({ member, regions, showings }) => (
        <div key={member.partner_user.id} className="flex w-max min-w-full">
          <div
            className="sticky left-0 z-20 flex shrink-0 items-center border-b border-r border-alpha-80 bg-white px-3"
            style={{ width: MEMBER_NAME_COL_PX, height: MEMBER_ROW_PX }}
          >
            <span className="truncate text-sm font-medium text-content-400">
              {member.partner_user.name}
            </span>
          </div>

          {/* Time track */}
          <div
            className="relative shrink-0 border-b border-alpha-80"
            style={{ width: timelineWidth, height: MEMBER_ROW_PX }}
          >
            {/* Hour gridlines */}
            {hourLabels.slice(1, -1).map((hour) => (
              <div
                key={hour}
                className="absolute inset-y-0 border-l border-alpha-80"
                style={{
                  left: minutesToPx((hour - bandStartHour) * 60, hourPx)
                }}
              />
            ))}

            {/* Availability regions: solid green for available, hatched
                with a cursor-following range/reason tooltip for unavailable */}
            {regions.map((region, index) => {
              if (region.state === "available")
                return (
                  <div
                    key={`${region.topMinutes}-${index}`}
                    className={cx("absolute inset-y-0", AVAILABLE_FILL_CLASS)}
                    style={{
                      left: minutesToPx(region.topMinutes, hourPx),
                      width: minutesToPx(region.durationMinutes, hourPx)
                    }}
                  />
                );

              const title = region.allDay
                ? "Unavailable all day"
                : `Unavailable ${region.startLabel} – ${region.endLabel}`;
              return (
                <UnavailableRegion
                  key={`${region.topMinutes}-${index}`}
                  left={minutesToPx(region.topMinutes, hourPx)}
                  width={minutesToPx(region.durationMinutes, hourPx)}
                  title={title}
                  subtitle={reasonCopy(region.reason, member.partner_user.name)}
                />
              );
            })}

            {/* Scheduled showing cards (view-only, non-interactive —
                pointer-events-none lets hover reach the region tooltip
                beneath), clipped to the band so out-of-band showings
                cannot paint over surrounding UI */}
            {showings.map((showing) => {
              const geometry = showingGeometry(
                showing.start_time,
                showing.end_time,
                timezone,
                bandStartHour,
                bandEndHour,
                hourPx
              );
              if (!geometry) return null;

              const display = getShowingDisplay(showing.status);
              const compact = geometry.widthPx < CARD_COMPACT_PX;
              return (
                <div
                  key={showing.id}
                  className={cx(
                    "pointer-events-none absolute inset-y-0 z-10 overflow-hidden rounded-md border border-alpha-100 shadow-sm",
                    compact ? "px-0.5 py-0" : "p-1",
                    display.bgClass
                  )}
                  style={{
                    left: geometry.leftPx,
                    width: geometry.widthPx
                  }}
                >
                  <p
                    className={cx(
                      "font-medium",
                      compact
                        ? "h-full text-[10px] leading-[1.15] [writing-mode:vertical-rl]"
                        : "truncate text-[11px] leading-tight",
                      display.textClass
                    )}
                  >
                    {display.label}
                  </p>
                  <Show if={!compact}>
                    <p className="truncate text-[10px] leading-tight text-content-500">
                      {formatTime(showing.start_time, timezone)} -{" "}
                      {formatTime(showing.end_time, timezone)}
                    </p>
                  </Show>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
